import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetCIUsForClient } from '../../../../api/client';
import {
  useGetAllKidTemplates,
  useGetKidTemplateIncompleteDocuments,
} from '../../../../api/kids/hooks/queries';
import {
  KidTemplateIncompleteDocumentsResponse,
  KidTemplateType,
  getKidTemplateTypeRank,
} from '../../../../api/kids/types';
import { useGetClientAdministrators } from '../../../../api/administrator';
import { useFunds } from '../../../../api/funds';
import { useAllFundManagersForClient } from '../../../../api/rs3-public/raptorPlatform';
import TokenService from '../../../../services/token-service';
import {
  createPageDetailsSelector,
  createSectionByIdSelector,
} from '../../../../redux/pages/selectors';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import { createResourceCacheKey } from '../../../layout/general/SimpleGeneralPageWrapper';
import { sortFundNames } from '../../../../utilities/customSorting';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  toggleControlBarShowing,
  updateControlBarComponents,
} from '../../../../redux/ui/controlbar/actions';
import CreateTemplatePanel from './components/CreateTemplatePanel';
import TemplateListPanel from './components/TemplateListPanel';
import GenericModal from '../../../ui/GenericModal';
import {
  buildTemplateEditPath,
  pickRawTemplateType,
  pickTemplateSetupStatus,
  resolveTemplateGroupingTypeKey,
  resolveTemplateSetupStatusFromCoverage,
} from './templateManagement.helpers';
import { resolveTemplateReferenceLabel } from './templateManagement.utils';
import { TemplateListTableRow } from './templateManagement.types';

const TemplateManagement: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = TokenService.getUser();
  const clientId = user?.user_client_id || '';
  const sectionDetails = useSelector(createSectionByIdSelector('raidr-v2'));
  const pageDetails = useSelector(
    createPageDetailsSelector('template-management', 'raidr-v2'),
  );
  const pageTitle = pageDetails?.pageTitle ?? 'Template Management';
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const rsResources = createResourceCacheKey(
    sectionDetails?.rsResources,
    pageDetails?.rsResources,
  );

  const documentLanguage: string = 'English';
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [pendingSelectTemplateName, setPendingSelectTemplateName] = useState<
    string | null
  >(null);

  const { data: allTemplatesData } = useGetAllKidTemplates(clientId);
  const { data: fundsData } = useFunds({
    fundTypes: sectionDetails?.fundTypes,
    rsResources,
    raptorPlatformId,
  });
  const { data: fundManagersData } = useAllFundManagersForClient(clientId);
  const { data: ciusData } = useGetCIUsForClient(clientId);
  const { data: administratorsData } = useGetClientAdministrators(clientId);

  const templates = useMemo(
    () => (Array.isArray(allTemplatesData) ? allTemplatesData : []),
    [allTemplatesData],
  );
  const availableFunds = useMemo(
    () =>
      [...(fundsData || [])].sort((a, b) =>
        sortFundNames(a.fund_name || '', b.fund_name || ''),
      ),
    [fundsData],
  );
  const availableFundManagers = useMemo(
    () =>
      [...(fundManagersData || [])].sort((a, b) =>
        a.fund_manager_name.localeCompare(b.fund_manager_name),
      ),
    [fundManagersData],
  );
  const availableCIUs = useMemo(
    () => [...(ciusData || [])].sort((a, b) => a.ciu_name.localeCompare(b.ciu_name)),
    [ciusData],
  );
  const availableAdministrators = useMemo(
    () =>
      [...(administratorsData || [])].sort((a, b) =>
        a.administrator_name.localeCompare(b.administrator_name),
      ),
    [administratorsData],
  );

  /* Legacy deep link: /template-management?templateId=… → edit page. */
  useEffect(() => {
    const legacyTemplateId = searchParams.get('templateId');
    if (!legacyTemplateId) return;
    navigate(buildTemplateEditPath(legacyTemplateId), { replace: true });
  }, [navigate, searchParams]);

  useEffect(() => {
    if (!pendingSelectTemplateName || !templates.length) return;
    const createdTemplate = templates.find(
      (template) => template.template_name === pendingSelectTemplateName,
    );
    if (!createdTemplate) return;
    setPendingSelectTemplateName(null);
    navigate(buildTemplateEditPath(createdTemplate.template_id));
  }, [templates, pendingSelectTemplateName, navigate]);

  const incompleteDocumentsQuery = useGetKidTemplateIncompleteDocuments(
    clientId,
    undefined,
    undefined,
    { enabled: !!clientId },
  );

  const incompleteDocuments: KidTemplateIncompleteDocumentsResponse =
    incompleteDocumentsQuery.data || [];

  const templateTypeFromIncompleteById = useMemo(() => {
    const m = new Map<string, unknown>();
    incompleteDocuments.forEach((entry) => {
      if (!entry.template_id || m.has(entry.template_id)) return;
      const raw = pickRawTemplateType(entry.template);
      if (raw !== undefined && raw !== null && raw !== '') {
        m.set(entry.template_id, raw);
      }
    });
    return m;
  }, [incompleteDocuments]);

  const incompleteEntryByTemplateId = useMemo(() => {
    const m = new Map<string, KidTemplateIncompleteDocumentsResponse[number]>();
    incompleteDocuments.forEach((entry) => {
      if (entry.template_id) m.set(entry.template_id, entry);
    });
    return m;
  }, [incompleteDocuments]);

  const templateSetupStatusFromIncompleteById = useMemo(() => {
    const m = new Map<string, string>();
    incompleteDocuments.forEach((entry) => {
      const setupStatus = pickTemplateSetupStatus(entry.template);
      if (entry.template_id && setupStatus) {
        m.set(entry.template_id, setupStatus);
      }
    });
    return m;
  }, [incompleteDocuments]);

  const documentCountByTemplateId = useMemo(() => {
    const map = new Map<
      string,
      { missing: number; invalid: number }
    >();
    incompleteDocuments.forEach((entry) => {
      map.set(entry.template_id, {
        missing: entry.missing_overviews.length,
        invalid: entry.invalid_overviews.length,
      });
    });
    return map;
  }, [incompleteDocuments]);

  const templateListRows = useMemo((): TemplateListTableRow[] => {
    const referenceOptions = {
      availableFunds,
      availableFundManagers,
      availableCIUs,
      availableAdministrators,
    };

    const typeRank = (typeKey: string) =>
      (Object.values(KidTemplateType) as string[]).includes(typeKey)
        ? getKidTemplateTypeRank(typeKey as KidTemplateType)
        : -1;

    return templates
      .map((template) => {
        const typeKey = resolveTemplateGroupingTypeKey(
          template,
          templateTypeFromIncompleteById.get(template.template_id),
        );

        const coverage = documentCountByTemplateId.get(template.template_id);
        const missingDocumentCount = coverage?.missing ?? 0;
        const invalidDocumentCount = coverage?.invalid ?? 0;

        return {
          template,
          missingDocumentCount,
          invalidDocumentCount,
          documentCount: missingDocumentCount + invalidDocumentCount,
          setupStatus: resolveTemplateSetupStatusFromCoverage({
            templateStatus:
              pickTemplateSetupStatus(template) ||
              templateSetupStatusFromIncompleteById.get(template.template_id) ||
              null,
            incompleteEntry: incompleteEntryByTemplateId.get(
              template.template_id,
            ),
            coverageKnown: incompleteDocumentsQuery.isSuccess,
          }),
          typeKey,
          appliesToLabel:
            typeKey === KidTemplateType.MANCO
              ? 'All documents'
              : resolveTemplateReferenceLabel(
                  typeKey,
                  template.reference_id,
                  referenceOptions,
                ),
        };
      })
      .sort((a, b) => {
        const rankDiff = typeRank(b.typeKey) - typeRank(a.typeKey);
        if (rankDiff !== 0) return rankDiff;
        return (a.template.template_name || a.template.template_id).localeCompare(
          b.template.template_name || b.template.template_id,
          undefined,
          { sensitivity: 'base' },
        );
      });
  }, [
    templates,
    documentCountByTemplateId,
    templateTypeFromIncompleteById,
    templateSetupStatusFromIncompleteById,
    incompleteEntryByTemplateId,
    incompleteDocumentsQuery.isSuccess,
    availableFunds,
    availableFundManagers,
    availableCIUs,
    availableAdministrators,
  ]);

  const selectTemplateForEdit = useCallback(
    (templateId: string) => {
      navigate(buildTemplateEditPath(templateId));
    },
    [navigate],
  );

  const startCreatingTemplate = useCallback(() => {
    setIsCreatingTemplate(true);
  }, []);

  const cancelCreateTemplate = useCallback(() => {
    setIsCreatingTemplate(false);
  }, []);

  const handleTemplateCreated = useCallback((templateName: string) => {
    setIsCreatingTemplate(false);
    setPendingSelectTemplateName(templateName);
  }, []);

  /* Drop legacy ?tab= from older links. */
  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (!prev.has('tab')) return prev;
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  useEffect(() => {
    dispatch(toggleControlBarShowing(true));
    dispatch(
      updateControlBarComponents([
        <div
          key="tm-controlbar"
          className="flex min-w-0 items-center gap-4 px-4 py-2"
        >
          <h1 className="text-xl font-semibold text-mainblue md:text-2xl">
            {pageTitle}
          </h1>
        </div>,
      ]),
    );
    return () => {
      dispatch(updateControlBarComponents([]));
      dispatch(toggleControlBarShowing(false));
    };
  }, [dispatch, pageTitle]);

  return (
    <div className="-m-2.5 flex h-[calc(100vh-8.75rem)] w-[calc(100%+1.25rem)] flex-col overflow-hidden text-sm text-gray-900">
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-3 pt-3">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <TemplateListPanel
            rows={templateListRows}
            isUpdatingCounts={incompleteDocumentsQuery.isFetching}
            onSelectTemplate={selectTemplateForEdit}
            onStartCreateTemplate={startCreatingTemplate}
          />
          <GenericModal
            isOpen={isCreatingTemplate}
            setIsOpen={(open) => {
              if (!open) cancelCreateTemplate();
            }}
            title="Create template"
            description="Set the scope and initial content. You can refine the template after it’s created."
            className="relative max-w-3xl"
            descriptionClassName="text-sm text-gray-600"
            closeOnOutsideClick={false}
          >
            {isCreatingTemplate ? (
              <CreateTemplatePanel
                key="create-template-modal"
                clientId={clientId}
                documentLanguage={documentLanguage}
                onCancel={cancelCreateTemplate}
                onCreated={handleTemplateCreated}
                availableFunds={availableFunds}
                availableFundManagers={availableFundManagers}
                availableCIUs={availableCIUs}
                availableAdministrators={availableAdministrators}
              />
            ) : null}
          </GenericModal>
        </div>
      </div>
    </div>
  );
};

export default TemplateManagement;
